<script lang="ts" setup>
import { computed, inject, toRefs } from 'vue';
import Slider from 'primevue/slider';
import InputNumber from 'primevue/inputnumber';
import { PercentGoalInterface } from '@/types';
import { PERCENT_GOAL_PARAMS } from '@/enum';

interface PropsInterface {
  goal: PercentGoalInterface;
  attendeeId: number;
}

const props = defineProps<PropsInterface>();
const { goal, attendeeId } = toRefs(props);
const update = inject('updatePercentageGoal') as (attendeeId: number, trialId: number, percentage: number) => void;

const sliderValue = computed({
  get: () => goal.value.percentage,
  set: (value) => {
    update(attendeeId.value, goal.value.treatment_goal_id, parseInt(value.toString()));
  },
});

const sliderAxisValues = ['0%', '25%', '50%', '75%', '100%'];
</script>

<template>
  <div class="percentage-slider-container flex flex-column justify-content-between w-full">
    <div class="percentage-input w-full">
      <label for="percentage-input">Percentage</label>
      <InputNumber
        id="percentage-input"
        v-model="sliderValue"
        suffix="%"
        :min="PERCENT_GOAL_PARAMS.MIN_ALLOWED"
        :max="PERCENT_GOAL_PARAMS.MAX_ALLOWED"
        placeholder="Enter percentage or use slider"
        class="flex flex-column align-items-start w-full p-0 py-2 font-bold"
        input-class="w-full"
        :disabled="!!goal.completed_at"
      />
    </div>
    <div class="percentage-slider w-full bg-white">
      <Slider
        v-model="sliderValue"
        :disabled="!!goal.completed_at"
        :min="PERCENT_GOAL_PARAMS.MIN_ALLOWED"
        :max="PERCENT_GOAL_PARAMS.MAX_ALLOWED"
      />
      <div class="slider-axis flex justify-content-between align-items-center">
        <span v-for="value in sliderAxisValues" :key="value" class="slider-axis-text">
          {{ value }}
        </span>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.percentage-slider-container {
  .percentage-input {
    border-radius: 3px;

    label {
      font-family: Rubik;
      font-style: normal;
      font-weight: 300;
      font-size: 12px;
      line-height: 12px;
      padding-bottom: 6px;
    }

    :deep(.p-inputnumber) {
      width: 100%;

      .p-inputnumber-input {
        font-family: Rubik;
        font-size: 14px;
        line-height: 18px;
        border: none;
        color: #3b4242;
      }
    }
  }

  .percentage-slider {
    border-radius: 6px;
    padding: 12px 6px;

    .slider-axis {
      margin-top: 8px;

      .slider-axis-text {
        font-family: Rubik;
        font-weight: 500;
        font-size: 14px;
        line-height: 18px;
        color: var(--text-color-secondary);
      }
    }

    :deep(.p-slider) {
      height: 6px;

      .p-slider-range {
        background: #8bcfcf;
      }

      .p-slider-handle {
        border: 2px solid #c4e8e8;
        box-shadow: 0px 4px 12px rgba(59, 66, 66, 0.12);
      }
    }
  }
}
</style>
